import Phaser from 'phaser';
import { WORLD_HEIGHT, WORLD_WIDTH } from '../../config';
import type { WorldState } from '../../sim/types';
import type { CollisionShapes, TrackView } from '../../sim/world';

/** Overlays de debug, togglables, alimentés UNIQUEMENT par l'état sim (donc déterministes). */
export interface DebugFlags {
  grid: boolean;
  /** Flèches vitesse (jaune) vs cap (blanc) — visualise le drift (M1). */
  velocityVectors: boolean;
  /** Panneau texte chiffré par voiture. */
  debugHud: boolean;
  /** Wireframe des corps Matter (M2). */
  collisionBodies: boolean;
  /** Portes de checkpoint + centerline + prochaine porte (M3). */
  checkpoints: boolean;
  /** Racing line de l'IA (M4). */
  racingLine: boolean;
  /** Traces de pneus quand le véhicule dérobe (drift / glisse). */
  skidmarks: boolean;
  /** Légende des touches de debug (panneau d'aide en haut à droite). */
  help: boolean;
}

export const DEFAULT_DEBUG_FLAGS: DebugFlags = {
  grid: false,
  velocityVectors: false,
  debugHud: true,
  collisionBodies: false,
  checkpoints: false,
  racingLine: false,
  skidmarks: true,
  help: true,
};

/** Touche → libellé + nom du flag dont on lit l'état ON/OFF. Sert au câblage clavier ET à la légende. */
export const DEBUG_KEYS: ReadonlyArray<{ key: string; label: string; flag: keyof DebugFlags }> = [
  { key: 'C', label: 'Collisions', flag: 'collisionBodies' },
  { key: 'V', label: 'Vecteurs vitesse', flag: 'velocityVectors' },
  { key: 'K', label: 'Checkpoints', flag: 'checkpoints' },
  { key: 'R', label: 'Racing line (IA)', flag: 'racingLine' },
  { key: 'T', label: 'Traces pneus', flag: 'skidmarks' },
  { key: 'G', label: 'Grille', flag: 'grid' },
  { key: 'H', label: 'HUD chiffré', flag: 'debugHud' },
];

const GRID_STEP = 64;

export class DebugOverlay {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly hud: Phaser.GameObjects.Text;
  private readonly help: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.g = scene.add.graphics().setDepth(1000); // espace monde (scroll avec la caméra)
    this.hud = scene.add
      .text(10, 10, '', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#a9f0d1',
        backgroundColor: 'rgba(0,0,0,0.4)',
        padding: { x: 6, y: 4 },
      })
      .setDepth(1001)
      .setScrollFactor(0);
    // Légende des touches, ancrée en haut à droite (origine à droite).
    this.help = scene.add
      .text(WORLD_WIDTH - 10, 10, '', {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#ffffff',
        align: 'left',
        backgroundColor: 'rgba(0,0,0,0.55)',
        padding: { x: 8, y: 6 },
      })
      .setOrigin(1, 0)
      .setDepth(1001)
      .setScrollFactor(0);
  }

  /** Textes UI (HUD + légende) — à rendre par une caméra fixe SANS zoom (cf. GameScene). */
  get uiObjects(): Phaser.GameObjects.GameObject[] {
    return [this.hud, this.help];
  }

  /** Graphique des overlays en espace-monde (grille, checkpoints, collisions, vecteurs). */
  get worldLayer(): Phaser.GameObjects.Graphics {
    return this.g;
  }

  draw(
    state: WorldState,
    flags: DebugFlags,
    track: TrackView | null = null,
    shapes: CollisionShapes | null = null,
    aiTargets: ReadonlyArray<{ x: number; y: number } | null> = [],
  ): void {
    const g = this.g;
    g.clear();

    if (flags.racingLine && track) {
      // Trajectoire de l'IA (la centerline en MVP) + cible de visée de chaque IA.
      g.lineStyle(2, 0xff3df0, 0.5);
      g.strokePoints(
        track.centerline.map((p) => new Phaser.Math.Vector2(p.x, p.y)),
        true,
      );
      for (let i = 0; i < state.cars.length; i++) {
        const t = aiTargets[i];
        if (!t) continue;
        const c = state.cars[i];
        g.lineStyle(1.5, 0xffffff, 0.6);
        g.lineBetween(c.x, c.y, t.x, t.y);
        g.fillStyle(0xff3df0, 1);
        g.fillCircle(t.x, t.y, 4);
      }
    }

    if (flags.grid) {
      g.lineStyle(1, 0xffffff, 0.06);
      for (let x = 0; x <= WORLD_WIDTH; x += GRID_STEP) g.lineBetween(x, 0, x, WORLD_HEIGHT);
      for (let y = 0; y <= WORLD_HEIGHT; y += GRID_STEP) g.lineBetween(0, y, WORLD_WIDTH, y);
    }

    if (flags.checkpoints && track) {
      const nextCp = state.cars.length > 0 ? (state.cars[0].checkpointIdx + 1) % track.gates.length : -1;
      for (const gate of track.gates) {
        const color = gate.index === 0 ? 0xff4081 : gate.index === nextCp ? 0x76ff03 : 0x00e5ff;
        g.lineStyle(gate.index === 0 || gate.index === nextCp ? 3 : 1.5, color, 0.9);
        g.lineBetween(gate.ax, gate.ay, gate.bx, gate.by);
      }
      // Centerline (jaune pâle).
      g.lineStyle(1, 0xffee58, 0.5);
      for (let i = 0; i < track.centerline.length; i++) {
        const a = track.centerline[i];
        const b = track.centerline[(i + 1) % track.centerline.length];
        g.lineBetween(a.x, a.y, b.x, b.y);
      }
    }

    if (flags.collisionBodies && shapes) {
      // Sommets RÉELS des corps Matter : murs (cyan) et voitures (orange).
      g.lineStyle(1.5, 0x00e5ff, 0.95);
      for (const poly of shapes.walls) strokePoly(g, poly);
      g.lineStyle(1.5, 0xff9100, 1);
      for (const poly of shapes.cars) strokePoly(g, poly);
    }

    if (flags.velocityVectors) {
      for (const c of state.cars) {
        g.lineStyle(2, 0xffffff, 0.9);
        g.lineBetween(c.x, c.y, c.x + Math.cos(c.angle) * 34, c.y + Math.sin(c.angle) * 34);
        g.lineStyle(2, 0xffe14a, 0.9);
        g.lineBetween(c.x, c.y, c.x + c.vx * 0.18, c.y + c.vy * 0.18);
      }
    }

    this.help.setVisible(flags.help);
    if (flags.help) {
      const rows = DEBUG_KEYS.map(
        ({ key, label, flag }) => `[${key}] ${flags[flag] ? '●' : '·'} ${label}`,
      );
      this.help.setText(['— DEBUG (F1) —', ...rows]);
    }

    this.hud.setVisible(flags.debugHud);
    if (flags.debugHud) {
      const totalLaps = track ? track.laps : 0;
      const lines = [`step ${state.step}  t=${state.time.toFixed(2)}s`];
      for (const c of state.cars) {
        const lap = track ? `${c.lap}/${totalLaps}` : `${c.lap}`;
        const ww = c.wrongWay ? '  CONTRESENS' : '';
        const last = c.lastLapTime > 0 ? `  last=${c.lastLapTime.toFixed(2)}s` : '';
        lines.push(
          `${c.id}  P${c.position}  lap ${lap}  spd=${c.speed.toFixed(0)}  ` +
            `slip=${c.lateralSlip.toFixed(0)}  grip=${c.grip.toFixed(2)}${last}${ww}`,
        );
      }
      this.hud.setText(lines);
    }
  }
}

function strokePoly(g: Phaser.GameObjects.Graphics, poly: ReadonlyArray<{ x: number; y: number }>): void {
  if (poly.length === 0) return;
  g.strokePoints(
    poly.map((p) => new Phaser.Math.Vector2(p.x, p.y)),
    true,
  );
}
